import { z } from "zod";

/**
 * Рабочее пространство преподавателя: файлы и папки.
 *
 * Личное хранилище, общей библиотеки нет и не планируется. Причина не
 * техническая: аудио из учебников защищено авторским правом, и выкладывание
 * его в общий доступ это прямой иск (раздел 8 CLAUDE.md).
 *
 * Перекодирования нет и не будет: ffmpeg-воркеры это отдельный проект. Значит
 * список принимаемых типов закрытый, и всё, что в него не попало, отклоняется
 * с внятным текстом, а не «съедается» и ломается потом на уроке.
 */

/**
 * Тип содержимого определяет, чем файл показывается, а не что с ним можно
 * делать. Отсюда `doc`: PDF и PPTX не показываются вовсе, они материал, из
 * которого собирают заготовку. PDF режется на страницы импортом, PPTX пока
 * только лежит.
 */
export const MediaKind = z.enum(["video", "audio", "image", "doc"]);
export type MediaKind = z.infer<typeof MediaKind>;

export interface MediaTypeSpec {
  kind: MediaKind;
  /** Расширения только для подсказки в диалоге выбора файла. Решает сигнатура. */
  extensions: string[];
  /** Как называть тип человеку в отказе. */
  title: string;
  /**
   * Производный тип: файл такого типа создаём МЫ, а человек его загрузить не
   * может.
   *
   * ⚠️ Признак существует ради `image/svg+xml` и ради одного конкретного
   * свойства: SVG это документ, а не картинка. Раздача ставит для картинок
   * `Content-Disposition: inline` (routes/media.ts), то есть загруженный
   * человеком SVG исполнил бы свои скрипты на нашем origin, с сессионной кукой
   * рядом. Наш собственный SVG безопасен по построению (внутри только `path`,
   * `line`, `text` и `g`, ни одного `<script>`), а чужой безопасным быть не
   * обязан. Отдельным рубежом на раздаче стоит CSP, но признак нужен и без
   * него: отказывать надо ДО заливки байт, а не после.
   *
   * Отказ приходит на СОЗДАНИИ сессии загрузки, потому что `MediaContentType`
   * производные не пропускает, а системный диалог их не предлагает, потому что
   * их не перечисляет `mediaAcceptAttribute`. `mediaKindOf` при этом их знает:
   * раздача обязана опознать тип и отдать его картинкой.
   */
  derived?: boolean;
}

/**
 * Единственный источник правды о типах файлов. Читают его пятеро: диалог выбора
 * файла (`mediaAcceptAttribute`), схема заявки на загрузку (`MediaContentType`),
 * определение вида при раздаче (`mediaKindOf`), клиентская проверка до отправки
 * (`features/media/probe.ts`) и проверка сигнатуры на сервере
 * (`media/sniff.ts`). Разъехавшиеся списки означали бы файл, принятый клиентом и
 * отвергнутый сервером после получаса заливки.
 *
 * ⚠️ Добавляя тип, пройдите всех пятерых. Производный тип (`derived`) задевает
 * их по-разному: трое обязаны его НЕ видеть, `mediaKindOf` обязан видеть, а
 * `sniff.ts` отвергает его тем, что не заводит проверку сигнатуры.
 */
export const MEDIA_TYPES: Readonly<Record<string, MediaTypeSpec>> = {
  "video/mp4": { kind: "video", extensions: [".mp4"], title: "MP4 (H.264 + AAC)" },
  "audio/mpeg": { kind: "audio", extensions: [".mp3"], title: "MP3" },
  "audio/mp4": { kind: "audio", extensions: [".m4a", ".mp4"], title: "M4A (AAC)" },
  "image/png": { kind: "image", extensions: [".png"], title: "PNG" },
  "image/jpeg": { kind: "image", extensions: [".jpg", ".jpeg"], title: "JPEG" },
  "image/webp": { kind: "image", extensions: [".webp"], title: "WebP" },
  "image/svg+xml": {
    kind: "image",
    extensions: [".svg"],
    title: "SVG",
    derived: true,
  },
  "application/pdf": { kind: "doc", extensions: [".pdf"], title: "PDF" },
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": {
    kind: "doc",
    extensions: [".pptx"],
    title: "PPTX",
  },
};

/** Типы, которые человек имеет право загрузить. Производных здесь нет. */
export function uploadableMediaTypes(): [string, MediaTypeSpec][] {
  return Object.entries(MEDIA_TYPES).filter(([, spec]) => spec.derived !== true);
}

export function isUploadableMediaType(contentType: string): boolean {
  const spec = MEDIA_TYPES[contentType];
  return spec !== undefined && spec.derived !== true;
}

export const MediaContentType = z.string().refine(isUploadableMediaType, {
  message: "тип файла не поддерживается",
});

/**
 * Вид файла по типу содержимого. Производные типы сюда ВХОДЯТ: раздача обязана
 * знать, что SVG графика это картинка, иначе он поедет вложением.
 */
export function mediaKindOf(contentType: string): MediaKind | null {
  return MEDIA_TYPES[contentType]?.kind ?? null;
}

/** Строка для атрибута `accept` у `<input type="file">`. */
export function mediaAcceptAttribute(): string {
  const parts: string[] = [];
  for (const [type, spec] of uploadableMediaTypes()) {
    parts.push(type, ...spec.extensions);
  }
  return parts.join(",");
}

/**
 * Имя файла или папки, как его видит человек.
 *
 * Путь на диске из него НЕ строится никогда: раскладка идёт по владельцу и
 * хешу содержимого. Поэтому здесь запрещены только управляющие символы и
 * разделители пути, и запрещены они ради читаемости списка, а не ради
 * безопасности: безопасность обеспечена тем, что это имя не попадает в путь.
 */
export const MediaName = z
  .string()
  .trim()
  .min(1)
  .max(200)
  .refine((v) => !/[\u0000-\u001f\u007f/\\]/.test(v), {
    message: "недопустимые символы в имени",
  });

export const MediaFolderName = MediaName.refine((v) => v.length <= 120, {
  message: "слишком длинное имя папки",
});

/**
 * Откуда папка взялась и можно ли её трогать.
 *
 * `plain` это папка человека, всё остальное заводит сервер сам под картинки,
 * брошенные на доску (`docs/room-feedback-fixes.md`, раздел 2.5). Служебную
 * папку нельзя переименовать, перенести и удалить: это защита от «удалил по
 * незнанию», а не украшение. Отдельные файлы внутри удаляются как обычно.
 *
 * ⚠️ Вид папки это НЕ признак «удалять нельзя», записанный другими словами.
 * Признак вычисляется по виду (`kind !== "plain"`), а сам вид отвечает ещё на
 * два вопроса: как папку показать и чем она заполняется.
 */
export const MediaFolderKind = z.enum([
  "plain",
  /** Корень «Уроки». Внутри папки занятий. */
  "lessons_root",
  /** Корень «Заготовки». Внутри папки заготовок. */
  "plans_root",
  "lesson",
  "plan",
  /**
   * «Материал к домашкам»: сюда ложатся фото, загруженные прямо в форме выдачи
   * (1.6 `docs/homework-scratch-design.md`). Служебная, а не `plain`: такую
   * переименуют, и следующая загрузка завела бы дубль по имени. Рядом живёт
   * «Домашние работы» с фото учеников, это другая папка.
   */
  "homework_material",
]);
export type MediaFolderKind = z.infer<typeof MediaFolderKind>;

export const MediaFolder = z.object({
  id: z.string().uuid(),
  /** null это корень. Отдельной записи под корень нет. */
  parentId: z.string().uuid().nullable(),
  name: z.string(),
  createdAt: z.number().int(),
  /**
   * `default` у трёх полей ниже держит совместимость со старым сервером,
   * который их ещё не отдаёт: открытая вкладка переживает выкатку (5.1
   * CLAUDE.md), и разбор ответа не должен падать на отсутствующем поле.
   */
  kind: MediaFolderKind.default("plain"),
  /** Зачем папка нужна. Есть только у служебных, человеку его не написать. */
  description: z.string().nullable().default(null),
  /**
   * Урок или заготовка, под которые папка заведена, исчезли.
   *
   * ⚠️ ОТДЕЛЬНОЕ поле, а не приписка к имени: приписка подралась бы с
   * переименованием, если папки уроков когда-нибудь станут переименовываемыми
   * (раздел 8 проекта). Сами папка и файлы при этом остаются: человек их не
   * удалял.
   */
  sourceDeleted: z.boolean().default(false),
});
export type MediaFolder = z.infer<typeof MediaFolder>;

export const MediaFile = z.object({
  id: z.string().uuid(),
  folderId: z.string().uuid().nullable(),
  name: z.string(),
  kind: MediaKind,
  contentType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  createdAt: z.number().int(),
  /**
   * Длительность и размер кадра приходят от клиента при завершении загрузки:
   * он всё равно открывает файл `<video>`, чтобы убедиться, что тот вообще
   * играет. Сервер их не проверяет и ни одного решения на них не строит, это
   * строка в списке.
   */
  durationMs: z.number().int().positive().nullable(),
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
  /**
   * Подписанный путь к превью, уже готовый для `<img src>`.
   *
   * Приезжает ВМЕСТЕ со списком, а не отдельным запросом на плитку. Двадцать
   * плиток это двадцать лишних кругов по сети, а круг на канале 128 кбит/с
   * стоит дороже, чем подпись в ответе (раздел 3.1 CLAUDE.md).
   *
   * null у файлов, залитых до появления превью: кадр снимается в браузере, пока
   * файл ещё в руках у преподавателя, и достроить его задним числом нечем.
   * `default(null)` держит совместимость со старым сервером, который поля ещё
   * не отдаёт.
   */
  previewPath: z.string().nullable().default(null),
  /**
   * Файл положил не владелец библиотеки, а ученик: картинка, брошенная им на
   * доску урока (2.5 docs/room-feedback-fixes.md).
   *
   * Признак, а не идентификатор автора: авторов у файла в чужой библиотеке
   * ровно два вида, «я» и «мой ученик», а имя ученика видно по папке занятия, в
   * которой файл лежит. Идентификатор здесь был бы полем, по которому нечего
   * спросить, зато можно случайно показать чужой id.
   */
  addedByStudent: z.boolean().default(false),
  /**
   * Страниц в документе по словам pdfium (`docs/pdf-book-view-design.md`, Р3).
   * Есть только у PDF и только когда число уже добыто: обложкой, подсчётом или
   * разбором. `default(null)` держит совместимость со старым сервером.
   */
  pageCount: z.number().int().positive().nullable().default(null),
});
export type MediaFile = z.infer<typeof MediaFile>;

/**
 * Сколько занято и сколько всего. Видно в интерфейсе всегда, а не в момент
 * отказа.
 *
 * Три числа, а не одно, и это не украшательство. Под незавершённую загрузку
 * бронируется ВЕСЬ заявленный размер файла, а не принятые байты: без брони
 * вперёд десять параллельных заливок пройдут проверку каждая и вместе
 * перекроют потолок, а выяснится это на середине последней. Поведение
 * правильное, но одно суммарное число его скрывает: человек видит «занято
 * 35 МБ» там, где на диске лежит 10 МБ недокачанного, и считает это ошибкой.
 * Поэтому бронь едет отдельным полем и подписывается отдельной строкой.
 *
 * `usedBytes` это то, что упирается в потолок, то есть сумма двух других.
 * Инвариант держит сервер, клиент его не пересчитывает.
 */
export const MediaQuota = z.object({
  usedBytes: z.number().int().nonnegative(),
  /** Завершённые файлы вне корзины. Реально занятое место. */
  fileBytes: z.number().int().nonnegative().default(0),
  /** Забронировано под незавершённые загрузки. Снимается отменой. */
  pendingBytes: z.number().int().nonnegative().default(0),
  limitBytes: z.number().int().nonnegative(),
});
export type MediaQuota = z.infer<typeof MediaQuota>;

/**
 * Содержимое одной папки плюс всё дерево целиком.
 *
 * Дерево отдаётся целиком, а не по уровням: у одного преподавателя это
 * десятки строк, а второй запрос на каждое раскрытие узла на мобильном канале
 * стоит дороже, чем весь список папок разом.
 */
export const MediaListResponse = z.object({
  folderId: z.string().uuid().nullable(),
  folders: z.array(MediaFolder),
  files: z.array(MediaFile),
  quota: MediaQuota,
});
export type MediaListResponse = z.infer<typeof MediaListResponse>;

export const CreateFolderRequest = z.object({
  name: MediaFolderName,
  parentId: z.string().uuid().nullable().optional(),
});
export type CreateFolderRequest = z.infer<typeof CreateFolderRequest>;

export const RenameRequest = z.object({ name: MediaName });
export type RenameRequest = z.infer<typeof RenameRequest>;

export const MoveRequest = z.object({ folderId: z.string().uuid().nullable() });
export type MoveRequest = z.infer<typeof MoveRequest>;

export const MoveFolderRequest = z.object({ parentId: z.string().uuid().nullable() });
export type MoveFolderRequest = z.infer<typeof MoveFolderRequest>;

// ------------------------------------------------------------ пакет операций

/**
 * Сколько объектов принимаем в одном пакете.
 *
 * Число названо здесь, а не спрятано в проверке на сервере, потому что о нём
 * обязан знать интерфейс: он выделяет мышью и вправе выделить всё. Отказ на
 * тысяче должен приходить до отправки и словами, а не пятисоткой после.
 */
export const MEDIA_BATCH_LIMIT = 200;

/**
 * Что попало в пакет. Файлы и папки разом: человек выделяет плитки, а не
 * «сначала файлы, потом папки».
 */
const MediaBatchItems = z.object({
  fileIds: z.array(z.string().uuid()).max(MEDIA_BATCH_LIMIT).default([]),
  folderIds: z.array(z.string().uuid()).max(MEDIA_BATCH_LIMIT).default([]),
});

function batchFits(v: { fileIds: string[]; folderIds: string[] }): boolean {
  const size = v.fileIds.length + v.folderIds.length;
  return size > 0 && size <= MEDIA_BATCH_LIMIT;
}

export const TrashBatchRequest = MediaBatchItems.refine(batchFits, {
  message: `от одного до ${MEDIA_BATCH_LIMIT} объектов за раз`,
});
export type TrashBatchRequest = z.infer<typeof TrashBatchRequest>;

/**
 * Перемещение пакетом. Один запрос на десять выделенных плиток, а не десять:
 * десять запросов по очереди на плохом канале это десять шансов оборваться
 * посередине и получить половину перемещённого.
 */
export const MoveBatchRequest = MediaBatchItems.extend({
  folderId: z.string().uuid().nullable(),
}).refine(batchFits, { message: `от одного до ${MEDIA_BATCH_LIMIT} объектов за раз` });
export type MoveBatchRequest = z.infer<typeof MoveBatchRequest>;

export const MediaBatchResult = z.object({
  files: z.number().int().nonnegative(),
  folders: z.number().int().nonnegative(),
});
export type MediaBatchResult = z.infer<typeof MediaBatchResult>;

export const TrashBatchResult = MediaBatchResult.extend({ groupId: z.string().uuid() });
export type TrashBatchResult = z.infer<typeof TrashBatchResult>;

// ------------------------------------------------------------------- корзина

/**
 * Строка корзины.
 *
 * Единица корзины это не файл, а ГРУППА: всё, что ушло одной операцией
 * удаления. Папка удаляется целиком вместе с содержимым, возвращается тоже
 * целиком, и тридцать суток считаются от момента удаления группы.
 *
 * ⚠️ Членство в группе фиксируется в момент удаления, а не вычисляется потом по
 * родителю. Файл, удалённый ОТДЕЛЬНО и раньше, лежит в корзине сам по себе, и
 * восстановление папки его не воскрешает: он про эту папку уже ничего не знает.
 *
 * `kind: "file"` это строка старого образца: файл, удалённый до появления групп.
 * Действия над ней идут прежними ручками по `key`, там лежит id файла.
 */
export const TrashEntry = z.object({
  key: z.string(),
  kind: z.enum(["group", "file"]),
  /** Имя корня удаления. По нему человек и узнаёт свою строку. */
  name: z.string(),
  /** Значок строки: удаляли папку или файл. */
  rootKind: z.enum(["file", "folder"]),
  /** Сколько ещё корней ушло вместе с первым. 0 значит «удалили один объект». */
  extraRoots: z.number().int().nonnegative().default(0),
  fileCount: z.number().int().nonnegative(),
  folderCount: z.number().int().nonnegative(),
  /** Сколько места вернётся при восстановлении. */
  sizeBytes: z.number().int().nonnegative(),
  deletedAt: z.number().int(),
  /** Когда сотрётся само. Считается от удаления группы, а не от файла. */
  purgeAt: z.number().int(),
  /** Превью, если в строке ровно один файл. Иначе значок по типу. */
  previewPath: z.string().nullable().default(null),
  fileKind: MediaKind.nullable().default(null),
});
export type TrashEntry = z.infer<typeof TrashEntry>;

/**
 * Ответ корзины.
 *
 * `files` это плоский список одиночных файлов, тот самый, что отдавался до
 * групп. Остался ради вкладки со старым бандлом, которая переживёт выкатку:
 * пусть у неё работает то, что работало, а не падает разбор ответа. Новый
 * клиент читает `entries`.
 */
export const TrashListResponse = z.object({
  entries: z.array(TrashEntry),
  files: z.array(MediaFile).default([]),
});
export type TrashListResponse = z.infer<typeof TrashListResponse>;

/**
 * Содержимое группы: посмотреть, что именно улетело. Действий над отдельными
 * элементами внутри нет намеренно, группа возвращается и стирается целиком.
 */
export const TrashGroupContents = z.object({
  folders: z.array(MediaFolder),
  files: z.array(MediaFile),
});
export type TrashGroupContents = z.infer<typeof TrashGroupContents>;

/**
 * Что получилось при восстановлении.
 *
 * Отказ на восстановлении это худший момент, чтобы требовать от человека
 * решений: он уже понял, что промахнулся, и хочет вернуть своё. Поэтому
 * исчезнувший родитель это корень, а занятое имя это суффикс, и обе поправки
 * не молчаливые, а названы в ответе.
 */
export const RestoreReport = z.object({
  files: z.number().int().nonnegative(),
  folders: z.number().int().nonnegative(),
  /** Легли в корень, потому что прежней папки уже нет. */
  toRoot: z.array(z.string()).default([]),
  /** Переименованы, потому что имя в папке уже занято. */
  renamed: z.array(z.object({ from: z.string(), to: z.string() })).default([]),
});
export type RestoreReport = z.infer<typeof RestoreReport>;

// ------------------------------------------------------------------- превью

/**
 * Длинная сторона превью. 320 px хватает плитке на экране с двойной
 * плотностью, а больше означало бы картинку тяжелее иного аудио.
 */
export const MEDIA_PREVIEW_EDGE = 320;

/**
 * Потолок на превью. Оно наше служебное и в квоту не идёт, поэтому потолок
 * здесь не про место, а про то, чтобы под видом превью не залили файл.
 */
export const MEDIA_PREVIEW_MAX_BYTES = 256 * 1024;

/**
 * Чем кодируем кадр. WebP основной, JPEG запасной: `canvas.toBlob` с WebP есть
 * не во всех Safari нашей цели (раздел build.target в vite.config), а молча
 * получить PNG на 400 КБ вместо превью хуже, чем получить JPEG.
 */
export const MEDIA_PREVIEW_TYPES = ["image/webp", "image/jpeg"] as const;

export const MediaPreviewResponse = z.object({ previewPath: z.string() });
export type MediaPreviewResponse = z.infer<typeof MediaPreviewResponse>;

// ------------------------------------------------------- где файл используется

/**
 * Где файл стоит. Данные уже есть в таблицах связей, отдельного учёта заводить
 * не пришлось.
 *
 * Это ответ на вопрос «а можно ли это удалить», который человек задаёт себе
 * каждый раз. Показывается в свойствах файла; при удалении спрашивается сразу
 * за всё выделенное, см. `MediaUsageDigest`.
 */
export const MediaUsage = z.object({
  plans: z.array(z.object({ id: z.string().uuid(), title: z.string() })),
  lessons: z.array(
    z.object({ id: z.string().uuid(), title: z.string(), createdAt: z.number().int() }),
  ),
  /**
   * Домашки, в которых файл задан картинкой (1.6
   * `docs/homework-scratch-design.md`). Любые существующие, включая корзину:
   * пока домашка есть, файл удалить нельзя. Умолчание держит старый сервер.
   */
  homeworks: z.array(z.object({ id: z.string().uuid(), title: z.string() })).default([]),
});
export type MediaUsage = z.infer<typeof MediaUsage>;

/**
 * Где стоит всё выделенное разом. Спрашивается ОДНИМ запросом перед удалением.
 *
 * Почему не по запросу на файл: десять выделенных плиток это десять кругов по
 * сети до показа диалога, а круг при 128 кбит/с стоит дороже всего остального
 * (раздел 3.1 CLAUDE.md). Заодно поштучный опрос не отвечает на вопрос про
 * папку: что лежит в её подпапках, клиент не знает вовсе, зато знает сервер.
 *
 * Списки усечены, а полное число рядом: сорок названий человек не читает, но
 * «и ещё 37» ему нужно честное.
 */
export const MediaUsageDigest = z.object({
  /** Сколько файлов из выделенного где-то стоят. Ноль означает «спрашивать не о чем». */
  files: z.number().int().nonnegative(),
  plans: z.array(z.object({ id: z.string().uuid(), title: z.string() })),
  plansTotal: z.number().int().nonnegative(),
  lessons: z.array(
    z.object({ id: z.string().uuid(), title: z.string(), createdAt: z.number().int() }),
  ),
  lessonsTotal: z.number().int().nonnegative(),
  /**
   * Домашки, держащие что-то из выделенного (1.6
   * `docs/homework-scratch-design.md`). ⚠️ Это не предупреждение, как уроки и
   * заготовки выше, а причина отказа: удаление с такими файлами ответит
   * `file_in_homework` целиком.
   */
  homeworks: z.array(z.object({ id: z.string().uuid(), title: z.string() })).default([]),
  homeworksTotal: z.number().int().nonnegative().default(0),
});
export type MediaUsageDigest = z.infer<typeof MediaUsageDigest>;

/** Сколько названий кладём в ответ. Остальное считается числом. */
export const MEDIA_USAGE_LIST_LIMIT = 20;

/**
 * Сколько названий показываем в предупреждении. Первые три отвечают на вопрос
 * «то ли это, что я думаю», остальное это «и ещё N».
 */
export const MEDIA_USAGE_NAMES = 3;

/**
 * Запрос «где стоит выделенное». Те же поля, что у удаления пакетом, и это не
 * совпадение: спрашивают ровно про то, что собираются удалить.
 */
export const UsageBatchRequest = MediaBatchItems.refine(batchFits, {
  message: `от одного до ${MEDIA_BATCH_LIMIT} объектов за раз`,
});
export type UsageBatchRequest = z.infer<typeof UsageBatchRequest>;

/**
 * Заявка на загрузку. Квота и тип проверяются ЗДЕСЬ, до первого байта: отказ
 * после получаса заливки по мобильному каналу это отказ от продукта.
 */
export const CreateUploadRequest = z.object({
  name: MediaName,
  contentType: MediaContentType,
  sizeBytes: z.number().int().positive(),
  folderId: z.string().uuid().nullable().optional(),
});
export type CreateUploadRequest = z.infer<typeof CreateUploadRequest>;

export const UploadSession = z.object({
  uploadId: z.string().uuid(),
  /** Сколько байт уже принято. После обрыва клиент продолжает отсюда. */
  offset: z.number().int().nonnegative(),
  sizeBytes: z.number().int().positive(),
  /** Размер куска выбирает сервер: он же знает свой лимит тела запроса. */
  chunkBytes: z.number().int().positive(),
});
export type UploadSession = z.infer<typeof UploadSession>;

/**
 * Незавершённая загрузка так, как её видит человек.
 *
 * Отдельная сущность от `UploadSession`: та отвечает на вопрос «откуда лить
 * дальше» и нужна работающему загрузчику, а эта на вопрос «что это за строка в
 * списке и почему у меня занято место». Без неё бронь в квоте невидима и
 * неуправляема до самой ночной уборки.
 */
export const PendingUpload = z.object({
  uploadId: z.string().uuid(),
  name: z.string(),
  kind: MediaKind,
  contentType: z.string(),
  /** Заявленный размер, он же размер брони в квоте. */
  sizeBytes: z.number().int().positive(),
  /** Сколько байт реально лежит на диске сервера. В строке показываем ЭТО. */
  receivedBytes: z.number().int().nonnegative(),
  folderId: z.string().uuid().nullable(),
  createdAt: z.number().int(),
  /** Когда последний раз двигалось смещение. Отвечает на «оно вообще живое?». */
  updatedAt: z.number().int(),
});
export type PendingUpload = z.infer<typeof PendingUpload>;

export const PendingUploadListResponse = z.object({ uploads: z.array(PendingUpload) });
export type PendingUploadListResponse = z.infer<typeof PendingUploadListResponse>;

/**
 * Размер блока в отпечатке принятой части. Общий для клиента и сервера: они
 * обязаны нарезать одни и те же байты одинаково, иначе отпечатки не сойдутся
 * никогда и продолжение станет невозможным вообще.
 */
export const UPLOAD_DIGEST_BLOCK_BYTES = 4 * 1024 * 1024;

/**
 * Отпечаток уже принятой части файла.
 *
 * Зачем он есть. После перезагрузки страницы браузер не может сам перечитать
 * файл с диска, и если объект File не нашёлся в IndexedDB (другое устройство,
 * другой браузер, вычищенное хранилище), человек выбирает файл заново. Имя и
 * размер тут ничего не доказывают: два разных монтажа одного урока сходятся и
 * по имени, и по размеру, а склейка двух разных файлов даёт битое видео,
 * которое пройдёт и проверку размера, и проверку сигнатуры.
 *
 * Почему хеш блоков, а не sha256 целиком. У WebCrypto нет потокового хеша, а
 * загонять 500 МБ принятой части в ArrayBuffer ради одного `digest()` значит
 * положить вкладку. Поэтому: sha256 каждого блока по `blockBytes`, затем
 * sha256 склеенных дайджестов. Память ограничена одним блоком с обеих сторон,
 * а покрыта при этом ВСЯ принятая часть, а не выборочный кусок.
 */
export const UploadFingerprint = z.object({
  uploadId: z.string().uuid(),
  /** Сколько байт покрыто отпечатком. Клиент берёт ровно этот префикс. */
  length: z.number().int().nonnegative(),
  blockBytes: z.number().int().positive(),
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
});
export type UploadFingerprint = z.infer<typeof UploadFingerprint>;

/**
 * Завершение. Длительность и размеры кадра клиент уже измерил, когда проверял,
 * что файл вообще открывается, поэтому отдаёт их здесь, а не заставляет сервер
 * поднимать ffprobe, которого у нас нет.
 */
export const CompleteUploadRequest = z.object({
  durationMs: z.number().int().positive().optional(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
});
export type CompleteUploadRequest = z.infer<typeof CompleteUploadRequest>;

/**
 * Короткоживущая подписанная ссылка на содержимое.
 *
 * Путь относительный, а не абсолютный адрес, и это осознанно: базовый адрес
 * API клиент и так знает из config.json, а зашитый в ответ хост означал бы
 * ещё одну переменную окружения, которая умеет разъехаться с реальностью
 * (в деве SPA на :5173, API на :3000, на проде оба на одном имени).
 */
export const MediaLinkResponse = z.object({
  path: z.string(),
  expiresAt: z.number().int(),
});
export type MediaLinkResponse = z.infer<typeof MediaLinkResponse>;
